import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  getProviderHealth,
  type ProviderHealth,
  type ProviderState,
} from '../lib/api/provider_health.js';

const STATE_STYLES: Record<ProviderState, string> = {
  healthy: 'bg-green-100 text-green-800 border-green-300',
  degraded: 'bg-amber-100 text-amber-900 border-amber-300',
  down: 'bg-red-100 text-red-800 border-red-300',
};

function ProviderChip({ provider }: { provider: ProviderHealth }) {
  const heldFor = provider.state_changed_at
    ? formatDistanceToNow(new Date(provider.state_changed_at), { addSuffix: false })
    : null;

  let label = provider.provider_name;
  if (provider.state === 'degraded') {
    label += ` (${provider.consecutive_failures} fails)`;
  } else if (provider.state === 'down' && heldFor) {
    label += ` (down ${heldFor})`;
  }

  const title =
    provider.state === 'healthy'
      ? `${provider.provider_name}: healthy`
      : `${provider.provider_name}: ${provider.state}\n` +
        `Failures: ${provider.consecutive_failures}\n` +
        (provider.last_error_class
          ? `Last error: ${provider.last_error_class} — ${provider.last_error_message ?? ''}`
          : '');

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${STATE_STYLES[provider.state]}`}
      title={title}
    >
      <span className="h-2 w-2 rounded-full bg-current opacity-75" aria-hidden />
      {label}
    </span>
  );
}

export function ProviderHealthBar() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['provider-health'],
    queryFn: getProviderHealth,
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  if (isLoading) {
    return <div className="hidden md:block text-xs text-slate-400">Loading provider health…</div>;
  }
  if (isError || !data) {
    return (
      <>
        <div className="hidden md:block text-xs text-red-500">Provider health unavailable</div>
        <span
          className="md:hidden h-2.5 w-2.5 rounded-full bg-red-400 flex-shrink-0"
          title="Provider health unavailable"
          aria-label="Provider health unavailable"
        />
      </>
    );
  }

  const unhealthy = data.providers.filter((p) => p.state !== 'healthy');
  const worst = data.providers.some((p) => p.state === 'down')
    ? 'down'
    : unhealthy.length > 0
      ? 'degraded'
      : 'healthy';
  const DOT_STYLES: Record<ProviderState, string> = {
    healthy: 'bg-green-400',
    degraded: 'bg-amber-400',
    down: 'bg-red-400',
  };
  const compactTitle =
    worst === 'healthy'
      ? 'All AI providers healthy'
      : unhealthy.map((p) => `${p.provider_name}: ${p.state}`).join('\n');

  return (
    <>
      {/* Full chip row — desktop/tablet */}
      <div className="hidden md:flex items-center gap-2">
        {data.providers.map((p) => (
          <ProviderChip key={p.provider_name} provider={p} />
        ))}
      </div>
      {/* Compact single-dot summary — phones (header space is scarce) */}
      <span
        className={`md:hidden inline-flex items-center gap-1 flex-shrink-0 ${
          worst === 'healthy' ? '' : 'rounded-full bg-surface/10 px-1.5 py-1'
        }`}
        title={compactTitle}
        aria-label={compactTitle}
      >
        <span className={`h-2.5 w-2.5 rounded-full ${DOT_STYLES[worst as ProviderState]}`} />
        {unhealthy.length > 0 && (
          <span className="text-xs font-semibold text-white">{unhealthy.length}</span>
        )}
      </span>
    </>
  );
}
